import React, { useState } from 'react';
import { CreateFlowModal } from './CreateFlowModal';
import { ModalContent } from './shared/FormComponents';
import { CreateFlowStepIndicator } from './shared/CreateFlowStepIndicator';
import { CreateFlowBackButton } from './shared/CreateFlowBackButton';
import { ContentTypeCard } from './shared/ContentTypeCard';
import { CreateContentMethodModal } from './CreateContentMethodModal';
import styled from 'styled-components';
import { Color } from 'core';
import { GoalType } from 'models/set';
import { getContentTypeDisplayName } from './shared/contentTypeUtils';
import { faDesktop, faCheckCircle, faChartBar } from '@fortawesome/free-solid-svg-icons';

interface IProps {
  onClose: () => void;
  onBack: () => void;
  onSetCreated?: () => void;
}

type ContentCreationStep = 'type-selection' | 'method-selection';

export const CreateContentTypeModal = (props: IProps) => {
  const { onClose, onBack, onSetCreated } = props;
  const [currentStep, setCurrentStep] = useState<ContentCreationStep>('type-selection');
  const [selectedGoalType, setSelectedGoalType] = useState<GoalType>('set');

  const handleContentTypeClick = (goalType: GoalType) => {
    setSelectedGoalType(goalType);
    setCurrentStep('method-selection');
  };

  const handleBackToTypeSelection = () => {
    setCurrentStep('type-selection');
  };

  if (currentStep === 'method-selection') {
    return (
      <CreateContentMethodModal
        goalType={selectedGoalType}
        onClose={onClose}
        onBack={handleBackToTypeSelection}
        onSetCreated={onSetCreated}
      />
    );
  }

  return (
    <CreateFlowModal title="Create Content (Select Type)" onClose={onClose}>
      <ModalContent>
        <CreateFlowBackButton onClick={onBack} />

        <CreateFlowStepIndicator activeStep={1} />
        <MainTitle>Select the type of content you'd like to create</MainTitle>

        <ContentTypeGrid>
          <ContentTypeCard
            title={getContentTypeDisplayName('set')}
            description="Adaptive content designed for repeated practice. Best when learners need long-term retention of knowledge or skills."
            icon={faDesktop}
            iconColor="#4A90E2"
            onClick={() => handleContentTypeClick('set')}
          />
          <ContentTypeCard
            title={getContentTypeDisplayName('assessment')}
            description="One-time pass through the material. Produces a score to measure knowledge at a moment in time."
            icon={faCheckCircle}
            iconColor="#5CB85C"
            onClick={() => handleContentTypeClick('assessment')}
          />
          <ContentTypeCard
            title={getContentTypeDisplayName('survey')}
            description="Unique question types for gathering feedback or polling a group. No adaptive review or scoring."
            icon={faChartBar}
            iconColor="#9B59B6"
            onClick={() => handleContentTypeClick('survey')}
          />
        </ContentTypeGrid>
      </ModalContent>
    </CreateFlowModal>
  );
};

const MainTitle = styled.h2`
  text-align: center;
  font-size: 24px;
  color: ${Color.primaryBlack};
  margin: 0 0 32px 0;
  font-weight: 600;
`;

const ContentTypeGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
